import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useArticles, useUpdateArticle } from '@/hooks/use-articles';
import type { Article } from '@erp/data-access/interfaces';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@erp/ui';
import { Input } from '@erp/ui';
import { Button } from '@erp/ui';
import { Search, Check } from '@/components/icons';

type AssignField = 'categoryId' | 'family' | 'brand';

interface ArticleAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The field on Article to assign */
  field: AssignField;
  /** The ID/value to assign */
  targetId: string;
  /** Dialog title */
  title: string;
}

export function ArticleAssignDialog({ open, onOpenChange, field, targetId, title }: ArticleAssignDialogProps) {
  const { t } = useTranslation();
  const { data: articles } = useArticles();
  const updateMutation = useUpdateArticle();
  const [search, setSearch] = useState('');

  const { assigned, unassigned } = useMemo(() => {
    const all = articles ?? [];
    const a: Article[] = [];
    const u: Article[] = [];
    for (const art of all) {
      if (art[field] === targetId) a.push(art);
      else u.push(art);
    }
    return { assigned: a, unassigned: u };
  }, [articles, field, targetId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return unassigned;
    const s = search.toLowerCase();
    return unassigned.filter((a) =>
      a.name.toLowerCase().includes(s) || a.code.toLowerCase().includes(s),
    );
  }, [unassigned, search]);

  const handleAssign = (articleId: string) => {
    updateMutation.mutate({ id: articleId, input: { [field]: targetId } });
  };

  const handleUnassign = (articleId: string) => {
    updateMutation.mutate({ id: articleId, input: { [field]: '' } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* Assigned articles */}
        {assigned.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">{t('articles.assignedArticles')} ({assigned.length})</p>
            <div className="max-h-[150px] overflow-y-auto rounded-lg border divide-y">
              {assigned.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {a.images?.[0] ? (
                      <img src={a.images[0]} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded bg-muted shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{a.name}</p>
                      <p className="text-[11px] text-muted-foreground">{a.code}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-destructive hover:text-destructive"
                    onClick={() => handleUnassign(a.id)}
                    disabled={updateMutation.isPending}
                  >
                    {t('articles.unassign')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search + available articles */}
        <div className="space-y-2 flex-1 min-h-0">
          <p className="text-xs font-medium text-muted-foreground">{t('articles.assignArticle')}</p>
          <div className="relative">
            <Search className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" style={{insetInlineStart:"0.6em"}} size={14} />
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-8"
            />
          </div>
          <div className="max-h-[250px] overflow-y-auto rounded-lg border divide-y">
            {filtered.length === 0 ? (
              <p className="text-center py-6 text-sm text-muted-foreground">{t('common.noResults')}</p>
            ) : filtered.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => handleAssign(a.id)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {a.images?.[0] ? (
                    <img src={a.images[0]} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded bg-muted shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm truncate">{a.name}</p>
                    <p className="text-[11px] text-muted-foreground">{a.code}</p>
                  </div>
                </div>
                <Check size={14} className="text-transparent group-hover:text-primary shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
